import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useToast } from '@/hooks/use-toast';
import {
  FinishingSoonRow,
  getAutoStatusLastRun,
  getFinishingSoonCases,
  groupByDoctor,
  keepCaseOpen,
} from '@/services/finishingSoonService';

const FINISHING_SOON_KEY = ['finishingSoon'] as const;
const LAST_RUN_KEY = ['autoStatusLastRun'] as const;

/** The Finishing soon list, per-doctor counts, the last run of the daily job, and Keep open. */
export const useFinishingSoon = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();

  const listQuery = useQuery({
    queryKey: FINISHING_SOON_KEY,
    queryFn: getFinishingSoonCases,
    staleTime: 5 * 60 * 1000,
  });
  const lastRunQuery = useQuery({
    queryKey: LAST_RUN_KEY,
    queryFn: getAutoStatusLastRun,
    staleTime: 5 * 60 * 1000,
  });

  const rows = useMemo<FinishingSoonRow[]>(() => listQuery.data ?? [], [listQuery.data]);
  const byCaseId = useMemo(() => new Map(rows.map((row) => [row.case_id, row])), [rows]);
  const doctors = useMemo(() => groupByDoctor(rows), [rows]);

  const keepOpenMutation = useMutation({
    mutationFn: ({ caseId }: { caseId: string }) => keepCaseOpen(caseId),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: FINISHING_SOON_KEY });
      toast({
        title: 'Kept open',
        description: 'This case will not close automatically.',
      });
    },
    onError: (error: unknown) => {
      toast({
        title: 'Could not keep this case open',
        description: error instanceof Error ? error.message : 'Please try again.',
        variant: 'destructive',
      });
    },
  });

  return {
    rows,
    byCaseId,
    doctors,
    count: rows.length,
    isLoading: listQuery.isLoading,
    error: listQuery.error,
    lastRun: lastRunQuery.data ?? null,
    keepOpen: (caseId: string) => keepOpenMutation.mutate({ caseId }),
    isKeepingOpen: keepOpenMutation.isPending,
  };
};
